import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Briefcase,
  Plus,
  Trash2,
  Link as LinkIcon,
  GripVertical,
  X,
  Eye,
  EyeOff,
  AlertCircle,
} from 'lucide-react';

import {
  CREATE_CUSTOM_SERVICE,
  DELETE_CUSTOM_SERVICE,
  GET_CUSTOM_SERVICES,
  SET_CUSTOM_SERVICE_STATUS,
  UPDATE_CUSTOM_SERVICE,
} from '../graphql/queries';
import { ImageUploader } from './ImageUploader';
import { SlidePanel } from './SlidePanel';

export interface CustomSubService {
  id?: string;
  name: string;
  description?: string | null;
  price: number;
  duration?: string | null;
  featured?: boolean;
  sortOrder?: number;
  status?: boolean;
}

export interface CustomServiceLink {
  id?: string;
  name?: string | null;
  url?: string | null;
  username?: string | null;
  password?: string | null;
  notes?: string | null;
}

export interface CustomServiceT {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  image: string | null;
  status: 'active' | 'draft' | 'archived';
  sortOrder: number;
  tags: string[];
  subServices: CustomSubService[];
  links: CustomServiceLink[];
  createdAt: string | null;
  updatedAt: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  draft: 'bg-amber-50 text-amber-700',
  archived: 'bg-gray-100 text-gray-600',
};

const formatPrice = (n?: number | null) => {
  if (n == null || isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

interface TagOption {
  id: string;
  name: string;
  color: string | null;
}

interface ServicesTabProps {
  showToast: (msg: string, type?: any) => void;
  tags?: TagOption[];
}

export const CustomServicesTab: React.FC<ServicesTabProps> = ({ showToast, tags = [] }) => {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CustomServiceT | 'new' | null>(null);

  const { data, loading, refetch } = useQuery(GET_CUSTOM_SERVICES, {
    variables: { search: search || undefined, pageNo: 0, limit: 200 },
    fetchPolicy: 'cache-and-network',
  });

  const services: CustomServiceT[] = data?.getCustomServices?.items ?? [];
  const error = data?.getCustomServices?.error ? data.getCustomServices.message : null;

  const [setStatus] = useMutation(SET_CUSTOM_SERVICE_STATUS);
  const [removeService] = useMutation(DELETE_CUSTOM_SERVICE);

  const handleStatus = async (s: CustomServiceT, status: 'active' | 'draft' | 'archived') => {
    const { data } = await setStatus({ variables: { id: s.id, status } });
    const r = data?.setCustomServiceStatus;
    if (r?.error) {
      showToast(r.message || 'Failed', 'error');
    } else {
      showToast(r?.message || 'Status updated', 'success');
      refetch();
    }
  };

  const handleDelete = async (s: CustomServiceT) => {
    if (!window.confirm(`Delete service "${s.name}"? This cannot be undone.`)) return;
    const { data } = await removeService({ variables: { id: s.id } });
    const r = data?.deleteCustomService;
    if (r?.error) {
      showToast(r.message || 'Failed', 'error');
    } else {
      showToast(r?.message || 'Service removed', 'success');
      refetch();
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center gap-2">
        <AlertCircle size={16} /> Couldn't load services: {error}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex-1 max-w-xs">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services…"
            className="input-field w-full"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-text3">
            {services.length} {services.length === 1 ? 'service' : 'services'}
          </span>
          <button onClick={() => setEditing('new')} className="btn-primary flex items-center gap-2">
            <Plus size={14} /> New Service
          </button>
        </div>
      </div>

      {loading && services.length === 0 ? (
        <div className="card p-12 text-center text-text3">Loading services…</div>
      ) : services.length === 0 ? (
        <div className="card p-12 text-center text-text3 flex flex-col items-center gap-3">
          <Briefcase size={28} className="text-text3" />
          <div className="text-sm font-bold text-text">No services yet</div>
          <div className="text-xs text-text3 max-w-xs">
            Create your first service. Add sub-services (with prices) and optional links FSRs can
            refer to in the field.
          </div>
          <button onClick={() => setEditing('new')} className="btn-primary mt-2 flex items-center gap-2">
            <Plus size={14} /> Add a service
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((s) => (
            <div key={s.id} className="card p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                  {s.image ? (
                    <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                  ) : (
                    <Briefcase size={22} className="text-text3" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-bold text-text truncate">{s.name}</h3>
                    <span className={`pill ${STATUS_COLORS[s.status] || ''} px-2`}>{s.status}</span>
                  </div>
                  {s.description && (
                    <p className="text-xs text-text3 line-clamp-2">{s.description}</p>
                  )}
                  <div className="text-[11px] text-text3 mt-1">
                    {s.subServices.length} sub-{s.subServices.length === 1 ? 'service' : 'services'} ·{' '}
                    {s.links.length} link{s.links.length === 1 ? '' : 's'}
                  </div>
                </div>
              </div>

              {s.subServices.length > 0 && (
                <div className="border-t border-border pt-3 space-y-1.5">
                  {s.subServices.slice(0, 3).map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-text2 truncate">
                        {sub.featured && <span className="text-primary mr-1">★</span>}
                        {sub.name}
                        {sub.duration && (
                          <span className="text-text3 font-normal"> · {sub.duration}</span>
                        )}
                      </span>
                      <span className="font-bold text-text">{formatPrice(sub.price)}</span>
                    </div>
                  ))}
                  {s.subServices.length > 3 && (
                    <div className="text-[10px] text-text3">+{s.subServices.length - 3} more</div>
                  )}
                </div>
              )}

              <div className="flex gap-1 pt-1 border-t border-border">
                <button
                  onClick={() => setEditing(s)}
                  className="flex-1 h-8 px-3 rounded-lg border border-border text-[12px] font-semibold text-text2 hover:bg-gray-50"
                >
                  Edit
                </button>
                {s.status !== 'active' ? (
                  <button
                    onClick={() => handleStatus(s, 'active')}
                    className="h-8 px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-100"
                  >
                    Publish
                  </button>
                ) : (
                  <button
                    onClick={() => handleStatus(s, 'draft')}
                    className="h-8 px-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-[12px] font-semibold hover:bg-amber-100"
                  >
                    Unpublish
                  </button>
                )}
                <button
                  onClick={() => handleDelete(s)}
                  className="h-8 px-3 rounded-lg border border-border text-red-600 text-[12px] font-semibold hover:bg-red-50 flex items-center gap-1"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SlidePanel
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        width={680}
        title={
          editing === 'new'
            ? 'New Service'
            : `Edit · ${(editing as CustomServiceT)?.name ?? ''}`
        }
        subtitle="Main service, sub-services and reference links"
      >
        {editing && (
          <ServiceEditPanel
            service={editing === 'new' ? null : (editing as CustomServiceT)}
            tags={tags}
            onClose={() => setEditing(null)}
            onSaved={(m) => {
              showToast(m, 'success');
              setEditing(null);
              refetch();
            }}
          />
        )}
      </SlidePanel>
    </>
  );
};

// ── Service edit slide-panel ────────────────────────────────────
const STATUS_OPTIONS: { id: 'active' | 'draft' | 'archived'; label: string }[] = [
  { id: 'draft', label: 'Draft' },
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
];

const emptySub = (): CustomSubService => ({
  name: '',
  description: '',
  price: 0,
  duration: '',
  featured: false,
  status: true,
});

const emptyLink = (): CustomServiceLink => ({
  name: '',
  url: '',
  username: '',
  password: '',
  notes: '',
});

const ServiceEditPanel: React.FC<{
  service: CustomServiceT | null;
  tags: TagOption[];
  onClose: () => void;
  onSaved: (m: string) => void;
}> = ({ service, tags, onClose, onSaved }) => {
  const isEdit = !!service;
  const [form, setForm] = useState({
    name: service?.name || '',
    description: service?.description || '',
    icon: service?.icon || '',
    image: service?.image || '',
    status: (service?.status as any) || 'draft',
    tags: Array.isArray(service?.tags) ? [...(service?.tags as string[])] : ([] as string[]),
    subServices: service?.subServices
      ? service.subServices.map((s) => ({ ...s }))
      : [emptySub()],
    links: service?.links ? service.links.map((l) => ({ ...l })) : ([] as CustomServiceLink[]),
  });

  const toggleTag = (name: string) =>
    setForm((f) =>
      f.tags.includes(name) ? { ...f, tags: f.tags.filter((t) => t !== name) } : { ...f, tags: [...f.tags, name] }
    );
  const [error, setError] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<number, boolean>>({});

  const [createService, { loading: creating }] = useMutation(CREATE_CUSTOM_SERVICE);
  const [updateService, { loading: updating }] = useMutation(UPDATE_CUSTOM_SERVICE);
  const saving = creating || updating;

  const updateSub = (i: number, patch: Partial<CustomSubService>) =>
    setForm((f) => {
      const subs = [...f.subServices];
      subs[i] = { ...subs[i], ...patch };
      return { ...f, subServices: subs };
    });

  const addSub = () =>
    setForm((f) => ({ ...f, subServices: [...f.subServices, emptySub()] }));

  const removeSub = (i: number) =>
    setForm((f) => ({ ...f, subServices: f.subServices.filter((_, idx) => idx !== i) }));

  const updateLink = (i: number, patch: Partial<CustomServiceLink>) =>
    setForm((f) => {
      const links = [...f.links];
      links[i] = { ...links[i], ...patch };
      return { ...f, links };
    });

  const addLink = () => setForm((f) => ({ ...f, links: [...f.links, emptyLink()] }));
  const removeLink = (i: number) =>
    setForm((f) => ({ ...f, links: f.links.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    setError(null);
    if (!form.name.trim()) {
      setError('Main service name is required');
      return;
    }
    const subs = form.subServices.filter((s) => s.name && s.name.trim());
    if (subs.length === 0) {
      setError('Add at least one sub-service with a name');
      return;
    }

    const input = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      icon: form.icon.trim() || null,
      image: form.image.trim() || null,
      status: form.status,
      tags: form.tags,
      subServices: subs.map((s) => ({
        ...(s.id ? { id: s.id } : {}),
        name: s.name.trim(),
        description: s.description?.trim() || null,
        price: Number(s.price) || 0,
        duration: s.duration?.trim() || null,
        featured: !!s.featured,
        status: s.status !== false,
      })),
      links: form.links
        .filter(
          (l) =>
            (l.name && l.name.trim()) ||
            (l.url && l.url.trim()) ||
            (l.username && l.username.trim()) ||
            (l.password && l.password) ||
            (l.notes && l.notes.trim())
        )
        .map((l) => ({
          ...(l.id ? { id: l.id } : {}),
          name: l.name?.trim() || null,
          url: l.url?.trim() || null,
          username: l.username?.trim() || null,
          password: l.password || null,
          notes: l.notes?.trim() || null,
        })),
    };

    try {
      if (isEdit && service) {
        const { data } = await updateService({ variables: { id: service.id, input } });
        const r = data?.updateCustomService;
        if (r?.error) {
          setError(r.message || 'Failed');
          return;
        }
        onSaved(r?.message || 'Service updated');
      } else {
        const { data } = await createService({ variables: { input } });
        const r = data?.createCustomService;
        if (r?.error) {
          setError(r.message || 'Failed');
          return;
        }
        onSaved(r?.message || 'Service created');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed');
    }
  };

  return (
    <div className="space-y-6 pb-4">
      {/* ── Main service ─────────────────────────────── */}
      <section>
        <h3 className="text-[11px] font-black uppercase tracking-widest text-text3 mb-3">
          Main Service
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">
              Main Service Name *
            </label>
            <input
              className="input-field mt-1 w-full"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-text3">
              Description
            </label>
            <textarea
              className="input-field mt-1 w-full h-[64px] py-2"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-text3">
                Icon (emoji or short text)
              </label>
              <input
                className="input-field mt-1 w-full"
                value={form.icon}
                onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="🧰  or  HVAC"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-text3">
                Status
              </label>
              <select
                className="input-field mt-1 w-full"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as any })}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ImageUploader
            label="Image"
            value={form.image || null}
            onChange={(url) => setForm({ ...form, image: url || '' })}
            size="lg"
            path="service"
          />
        </div>
      </section>

      <hr className="border-border" />

      {/* ── Tags (collection membership) ────────────── */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-text3">
            Tags
          </h3>
          <span className="text-[10px] text-text3">
            {form.tags.length} selected · drives collection membership
          </span>
        </div>
        {tags.length === 0 ? (
          <div className="text-xs text-text3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            No tags available. Add tags in the <span className="font-bold">Tags</span> tab first.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => {
              const selected = form.tags.includes(t.name);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.name)}
                  className={`flex items-center gap-1.5 px-3 h-8 rounded-full border text-[12px] font-semibold transition
                    ${selected
                      ? 'bg-primary text-white border-primary shadow-sm'
                      : 'bg-white text-text2 border-border hover:border-primary/40'}`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: t.color || (selected ? '#fff' : '#94A3B8') }}
                  />
                  #{t.name}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <hr className="border-border" />

      {/* ── Sub Services ─────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-text3">
            Sub Services
          </h3>
          <button
            type="button"
            onClick={addSub}
            className="text-xs font-semibold text-primary hover:underline"
          >
            + Add Sub Service
          </button>
        </div>
        <div className="space-y-3">
          {form.subServices.map((sub, i) => (
            <div key={i} className="border border-border rounded-xl p-3 space-y-2 bg-gray-50/40">
              <div className="flex items-start gap-2">
                <GripVertical size={14} className="text-text3 mt-2.5" />
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                      Sub Service Name *
                    </label>
                    <input
                      className="input-field mt-0.5 w-full h-9 text-sm"
                      value={sub.name}
                      onChange={(e) => updateSub(i, { name: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                      Description
                    </label>
                    <input
                      className="input-field mt-0.5 w-full h-9 text-sm"
                      value={sub.description || ''}
                      onChange={(e) => updateSub(i, { description: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      className="input-field mt-0.5 w-full h-9 text-sm"
                      value={sub.price ?? ''}
                      onChange={(e) =>
                        updateSub(i, { price: e.target.value === '' ? 0 : Number(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                      Duration
                    </label>
                    <input
                      className="input-field mt-0.5 w-full h-9 text-sm"
                      value={sub.duration || ''}
                      onChange={(e) => updateSub(i, { duration: e.target.value })}
                      placeholder="30 min · 1 hr · 2 sessions"
                    />
                  </div>
                  <div className="col-span-2 flex items-center justify-between pt-1">
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-text2">
                      <input
                        type="checkbox"
                        checked={!!sub.featured}
                        onChange={(e) => updateSub(i, { featured: e.target.checked })}
                      />
                      Featured
                    </label>
                    <button
                      type="button"
                      onClick={() => removeSub(i)}
                      className="h-7 px-2 rounded-lg border border-border text-red-600 text-[11px] font-semibold hover:bg-red-50"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <hr className="border-border" />

      {/* ── Links ─────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-text3">
            Links <span className="text-text3 font-normal normal-case tracking-normal">(optional)</span>
          </h3>
          <button
            type="button"
            onClick={addLink}
            className="text-xs font-semibold text-primary hover:underline"
          >
            + Add Link
          </button>
        </div>
        <p className="text-[11px] text-text3 mb-3">
          Reference links FSRs can use (partner portals, scheduling tools, customer accounts).
          Passwords are masked-by-default on the mobile detail screen.
        </p>
        {form.links.length === 0 && (
          <div className="text-xs text-text3 text-center bg-gray-50 border border-border rounded-lg py-6">
            No links yet.
          </div>
        )}
        <div className="space-y-3">
          {form.links.map((link, i) => {
            const showPw = !!showPasswords[i];
            return (
              <div key={i} className="border border-border rounded-xl p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <LinkIcon size={14} className="text-text3 mt-2.5" />
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                        Link Name
                      </label>
                      <input
                        className="input-field mt-0.5 w-full h-9 text-sm"
                        value={link.name || ''}
                        onChange={(e) => updateLink(i, { name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                        URL
                      </label>
                      <input
                        className="input-field mt-0.5 w-full h-9 text-sm"
                        value={link.url || ''}
                        onChange={(e) => updateLink(i, { url: e.target.value })}
                        placeholder="https://…"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                        Username
                      </label>
                      <input
                        className="input-field mt-0.5 w-full h-9 text-sm"
                        value={link.username || ''}
                        onChange={(e) => updateLink(i, { username: e.target.value })}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                        Password
                      </label>
                      <div className="relative mt-0.5">
                        <input
                          type={showPw ? 'text' : 'password'}
                          className="input-field w-full h-9 text-sm pr-16"
                          value={link.password || ''}
                          onChange={(e) => updateLink(i, { password: e.target.value })}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowPasswords((p) => ({ ...p, [i]: !showPw }))
                          }
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 rounded-md text-[11px] font-semibold text-text2 hover:bg-gray-100 flex items-center gap-1"
                        >
                          {showPw ? <EyeOff size={12} /> : <Eye size={12} />}
                          {showPw ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <label className="text-[9px] font-bold uppercase tracking-widest text-text3">
                        Notes
                      </label>
                      <textarea
                        className="input-field mt-0.5 w-full text-sm py-1.5"
                        value={link.notes || ''}
                        onChange={(e) => updateLink(i, { notes: e.target.value })}
                        rows={2}
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeLink(i)}
                        className="h-7 px-2 rounded-lg border border-border text-red-600 text-[11px] font-semibold hover:bg-red-50"
                      >
                        <X size={12} /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="flex gap-2 pt-2 sticky bottom-0 bg-white -mx-6 px-6 py-3 border-t border-border">
        <button
          onClick={onClose}
          disabled={saving}
          className="h-10 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-semibold text-text2"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={saving}
          className="flex-1 btn-primary disabled:opacity-60"
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create service'}
        </button>
      </div>
    </div>
  );
};
